import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import Layout from '@/components/Layout';
import { vehiclesApi } from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { toast } from 'sonner';
import { Plus, Search, Car, Eye, Edit, Trash2, Loader2 } from 'lucide-react';

const VEHICLE_TYPES = ['sedan', 'suv', 'bus', 'tempo', 'hatchback', 'minivan'];
const FUEL_TYPES = ['petrol', 'diesel', 'cng', 'ev', 'hybrid'];

const initialFormState = {
  registration_number: '',
  make: '',
  model: '',
  year: new Date().getFullYear(),
  vehicle_type: '',
  fuel_type: '',
  seating_capacity: 5,
  purchase_date: '',
  purchase_price: '',
  insurance_expiry: '',
  permit_expiry: '',
  fitness_expiry: '',
  puc_expiry: '',
  status: 'active',
};

const Vehicles = () => {
  const [vehicles, setVehicles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [formData, setFormData] = useState(initialFormState);
  const [editingId, setEditingId] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const fetchVehicles = async () => {
    try {
      const response = await vehiclesApi.getAll();
      setVehicles(response.data);
    } catch (error) {
      toast.error('Failed to fetch vehicles');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchVehicles();
  }, []);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSelectChange = (name, value) => {
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);

    try {
      const data = {
        ...formData,
        year: parseInt(formData.year),
        seating_capacity: parseInt(formData.seating_capacity),
        purchase_price: parseFloat(formData.purchase_price),
      };

      if (editingId) {
        await vehiclesApi.update(editingId, data);
        toast.success('Vehicle updated successfully');
      } else {
        await vehiclesApi.create(data);
        toast.success('Vehicle added successfully');
      }

      setDialogOpen(false);
      setFormData(initialFormState);
      setEditingId(null);
      fetchVehicles();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    } finally {
      setSubmitting(false);
    }
  };

  const handleEdit = (vehicle) => {
    setFormData({
      registration_number: vehicle.registration_number,
      make: vehicle.make,
      model: vehicle.model,
      year: vehicle.year,
      vehicle_type: vehicle.vehicle_type,
      fuel_type: vehicle.fuel_type,
      seating_capacity: vehicle.seating_capacity,
      purchase_date: vehicle.purchase_date,
      purchase_price: vehicle.purchase_price,
      insurance_expiry: vehicle.insurance_expiry || '',
      permit_expiry: vehicle.permit_expiry || '',
      fitness_expiry: vehicle.fitness_expiry || '',
      puc_expiry: vehicle.puc_expiry || '',
      status: vehicle.status,
    });
    setEditingId(vehicle.id);
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this vehicle?')) return;

    try {
      await vehiclesApi.delete(id);
      toast.success('Vehicle deleted successfully');
      fetchVehicles();
    } catch (error) {
      toast.error('Failed to delete vehicle');
    }
  };

  const filteredVehicles = vehicles.filter((v) =>
    v.registration_number.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.make.toLowerCase().includes(searchQuery.toLowerCase()) ||
    v.model.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const getStatusBadge = (status) => {
    const variants = {
      active: 'bg-emerald-100 text-emerald-700 border-emerald-200',
      maintenance: 'bg-yellow-100 text-yellow-700 border-yellow-200',
      inactive: 'bg-slate-100 text-slate-700 border-slate-200',
    };
    return (
      <Badge className={`${variants[status]} capitalize`}>
        {status}
      </Badge>
    );
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-IN', {
      style: 'currency',
      currency: 'INR',
      maximumFractionDigits: 0,
    }).format(amount || 0);
  };

  return (
    <Layout
      title="Vehicles"
      subtitle={`${vehicles.length} vehicles in fleet`}
      actions={
        <Dialog open={dialogOpen} onOpenChange={(open) => {
          setDialogOpen(open);
          if (!open) {
            setFormData(initialFormState);
            setEditingId(null);
          }
        }}>
          <DialogTrigger asChild>
            <Button className="bg-[#D4AF37] hover:bg-[#B5952F] text-black" data-testid="add-vehicle-btn">
              <Plus className="w-4 h-4 mr-2" />
              Add Vehicle
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle style={{ fontFamily: 'Playfair Display, serif' }}>
                {editingId ? 'Edit Vehicle' : 'Add New Vehicle'}
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-6 mt-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="registration_number">Registration Number *</Label>
                  <Input
                    id="registration_number"
                    name="registration_number"
                    placeholder="e.g., MH01AB1234"
                    value={formData.registration_number}
                    onChange={handleInputChange}
                    required
                    data-testid="vehicle-registration-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="vehicle_type">Vehicle Type *</Label>
                  <Select
                    value={formData.vehicle_type}
                    onValueChange={(v) => handleSelectChange('vehicle_type', v)}
                  >
                    <SelectTrigger data-testid="vehicle-type-select">
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {VEHICLE_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className="capitalize">
                          {type}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="make">Make *</Label>
                  <Input
                    id="make"
                    name="make"
                    placeholder="e.g., Toyota"
                    value={formData.make}
                    onChange={handleInputChange}
                    required
                    data-testid="vehicle-make-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model *</Label>
                  <Input
                    id="model"
                    name="model"
                    placeholder="e.g., Innova Crysta"
                    value={formData.model}
                    onChange={handleInputChange}
                    required
                    data-testid="vehicle-model-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="year">Year *</Label>
                  <Input
                    id="year"
                    name="year"
                    type="number"
                    min="2000"
                    max={new Date().getFullYear() + 1}
                    value={formData.year}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="fuel_type">Fuel Type *</Label>
                  <Select
                    value={formData.fuel_type}
                    onValueChange={(v) => handleSelectChange('fuel_type', v)}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select fuel type" />
                    </SelectTrigger>
                    <SelectContent>
                      {FUEL_TYPES.map((type) => (
                        <SelectItem key={type} value={type} className="capitalize">
                          {type.toUpperCase()}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="seating_capacity">Seating Capacity *</Label>
                  <Input
                    id="seating_capacity"
                    name="seating_capacity"
                    type="number"
                    min="2"
                    max="60"
                    value={formData.seating_capacity}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="purchase_date">Purchase Date *</Label>
                  <Input
                    id="purchase_date"
                    name="purchase_date"
                    type="date"
                    value={formData.purchase_date}
                    onChange={handleInputChange}
                    required
                  />
                </div>
                <div className="space-y-2 col-span-2">
                  <Label htmlFor="purchase_price">Purchase Price (₹) *</Label>
                  <Input
                    id="purchase_price"
                    name="purchase_price"
                    type="number"
                    min="0"
                    placeholder="e.g., 1500000"
                    value={formData.purchase_price}
                    onChange={handleInputChange}
                    required
                  />
                </div>
              </div>

              <div className="border-t pt-4">
                <h4 className="font-medium mb-3">Compliance Documents</h4>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor="insurance_expiry">Insurance Expiry</Label>
                    <Input
                      id="insurance_expiry"
                      name="insurance_expiry"
                      type="date"
                      value={formData.insurance_expiry}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="permit_expiry">Permit Expiry</Label>
                    <Input
                      id="permit_expiry"
                      name="permit_expiry"
                      type="date"
                      value={formData.permit_expiry}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="fitness_expiry">Fitness Expiry</Label>
                    <Input
                      id="fitness_expiry"
                      name="fitness_expiry"
                      type="date"
                      value={formData.fitness_expiry}
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="puc_expiry">PUC Expiry</Label>
                    <Input
                      id="puc_expiry"
                      name="puc_expiry"
                      type="date"
                      value={formData.puc_expiry}
                      onChange={handleInputChange}
                    />
                  </div>
                </div>
              </div>

              <div className="flex justify-end gap-3 pt-4 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => setDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={submitting}
                  className="bg-[#D4AF37] hover:bg-[#B5952F] text-black"
                  data-testid="vehicle-submit-btn"
                >
                  {submitting ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Saving...
                    </>
                  ) : editingId ? (
                    'Update Vehicle'
                  ) : (
                    'Add Vehicle'
                  )}
                </Button>
              </div>
            </form>
          </DialogContent>
        </Dialog>
      }
    >
      <div className="space-y-6" data-testid="vehicles-page">
        {/* Search */}
        <div className="flex items-center gap-4">
          <div className="relative flex-1 max-w-md">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
            <Input
              placeholder="Search vehicles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-10"
              data-testid="vehicle-search-input"
            />
          </div>
        </div>

        {/* Table */}
        <div className="bg-white rounded-xl border border-slate-200 overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="bg-slate-50">
                <TableHead>Registration</TableHead>
                <TableHead>Vehicle</TableHead>
                <TableHead>Type</TableHead>
                <TableHead>Fuel</TableHead>
                <TableHead>Purchase Price</TableHead>
                <TableHead>Status</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Loader2 className="w-8 h-8 animate-spin mx-auto text-slate-400" />
                  </TableCell>
                </TableRow>
              ) : filteredVehicles.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={7} className="text-center py-12">
                    <Car className="w-12 h-12 mx-auto text-slate-300 mb-3" />
                    <p className="text-slate-500">No vehicles found</p>
                    <p className="text-sm text-slate-400">Add your first vehicle to get started</p>
                  </TableCell>
                </TableRow>
              ) : (
                filteredVehicles.map((vehicle) => (
                  <TableRow key={vehicle.id} className="hover:bg-slate-50">
                    <TableCell className="font-medium">{vehicle.registration_number}</TableCell>
                    <TableCell>
                      <div>
                        <p className="font-medium">{vehicle.make} {vehicle.model}</p>
                        <p className="text-sm text-slate-500">{vehicle.year}</p>
                      </div>
                    </TableCell>
                    <TableCell className="capitalize">{vehicle.vehicle_type}</TableCell>
                    <TableCell className="uppercase">{vehicle.fuel_type}</TableCell>
                    <TableCell className="tabular-nums">{formatCurrency(vehicle.purchase_price)}</TableCell>
                    <TableCell>{getStatusBadge(vehicle.status)}</TableCell>
                    <TableCell>
                      <div className="flex items-center justify-end gap-2">
                        <Link to={`/vehicles/${vehicle.id}`}>
                          <Button variant="ghost" size="sm" data-testid={`view-vehicle-${vehicle.id}`}>
                            <Eye className="w-4 h-4" />
                          </Button>
                        </Link>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(vehicle)}
                          data-testid={`edit-vehicle-${vehicle.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(vehicle.id)}
                          className="text-red-500 hover:text-red-700"
                          data-testid={`delete-vehicle-${vehicle.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </div>
    </Layout>
  );
};

export default Vehicles;
